import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserAnalysisChartSelector, useUserAnalysisSelector } from "src/context/monitoring/userAnalysisContext";
import { numberWithCommas } from "src/services/utils";

const getOption = () => ({
  chart: {
    height: 260,
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: false,
    },
  },
  yAxis: {
    tickAmount: 6,
    labels: {
      enabled: false,
    },
  },
  ...beautifulTooltip((value) => value),
});

export default function UserActivenessOverview() {
  const { pool } = useParams();
  const { fetchUserActiveness } = useUserAnalysisSelector((state) => state.fetch);
  const userActiveness = useUserAnalysisChartSelector(MONITORING.ANALYSIS.userActiveness, pool);

  useEffect(() => {
    fetchUserActiveness(pool, { number: 360, interval: 2 }, { active: 2, isCheckMetadata: false });
  }, [pool]);

  const { series, isLoading } = useMemo(() => {
    if (userActiveness) {
      return {
        series: [
          {
            name: "Active Users",
            data: userActiveness.activeUsers.map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "Borrowing Users",
            data: userActiveness.borrowingUsers.map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "Deposit-only Users",
            data: userActiveness.depositOnlyUsers.map((element) => {
              return [].concat(element);
            }),
          },
        ],
        isLoading: false,
      };
    }
    return {
      series: [
        {
          name: "Active Users",
          data: [],
        },
        {
          name: "Borrowing Users",
          data: [],
        },
        {
          name: "Deposit-only Users",
          data: [],
        },
      ],
      isLoading: true,
    };
  }, [userActiveness]);

  const { lastActiveUser, lastBorrowingUser, lastDepositOnlyUser, lastDate } = useMemo(() => {
    if (userActiveness) {
      const date = Highcharts.dateFormat("%b %e, %Y, %k:%M", userActiveness.activeUsers.slice(-1)[0][0]);
      return {
        lastActiveUser: userActiveness.activeUsers.slice(-1)[0][1],
        lastBorrowingUser: userActiveness.borrowingUsers.slice(-1)[0][1],
        lastDepositOnlyUser: userActiveness.depositOnlyUsers.slice(-1)[0][1],
        lastDate: date,
      };
    }
    return {
      lastActiveUser: 0,
      lastBorrowingUser: 0,
      lastDepositOnlyUser: 0,
      lastDate: "",
    };
  }, [userActiveness]);

  return (
    <OverviewMonitoringWrapper
      isLoading={isLoading}
      title="User Activeness"
      overview="The chart shows the number of active users, deposit-only users, and borrowing users over time."
      to={`/user-analysis/user-activeness/${pool}`}
    >
      <Box>
        <Box display="flex" flexWrap="wrap">
          <FigureTypography
            value={numberWithCommas(lastActiveUser)}
            title="Active User"
            style={{ marginRight: "1rem" }}
          />
          <FigureTypography
            index={1}
            value={numberWithCommas(lastBorrowingUser)}
            title="Borrowing User"
            style={{ marginRight: "1rem" }}
          />
          <FigureTypography index={2} value={numberWithCommas(lastDepositOnlyUser)} title="Deposit Only User" />
        </Box>
        <Typography>{lastDate}</Typography>
      </Box>
      <LineChart series={series} option={getOption()} />
    </OverviewMonitoringWrapper>
  );
}
